import { PayloadAction } from '@reduxjs/toolkit';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, Observer } from 'rxjs';
import { delay, filter, mergeMap, throttleTime } from 'rxjs/operators';

import config from '../../../config';
import { MOCK } from '../../../configDebug';
import configLib from '../../../configLib';
import { PredefinedMilisecondsTypeEnum } from '../../enums';
import { DISCONNECT_ON_APP_MINIMISE_OR_LOCK, RECONNECT_TAPI_AND_GW_ON_GW_DOWN, SHOW_UI_AFTER_CONNECTED_DELAY } from '../../libSettings';
import { CacheService } from '../../services/cache/Cache.service';
import GlobalService from '../../services/Global.service';
import { LoggerService } from '../../services/logger/Logger.service';
import { ISSubscriptionService } from '../../services/subscription/ISSubscription.service';
import { MDSSubscriptionService } from '../../services/subscription/MDSSubscription.service';
import { MDSTSubscriptionService } from '../../services/subscription/MDSTSubscription.service';
import { NBBOSubscriptionService } from '../../services/subscription/NBBOSubscription.service';
import { NewsSubscriptionService } from '../../services/subscription/NewsSubscription.service';
import { SymbolNewsSubscriptionService } from '../../services/subscription/SymbolNewsSubscription.service';
import { SymbolStatusSubscriptionService } from '../../services/subscription/SymbolStatusSubscription.service';
import { clearUserEventsLastEvent } from '../../services/user-events/last-event-storage';
import { GatewayWebSocketService } from '../../services/web-socket/GatewayWebSocket.service';
import AppStateCache, { AppStateStatus } from '../../store-util/AppStateCache';
import CustomerState from '../../store-util/CustomerState';
import LoaderState from '../../store-util/LoaderState';
import { isEmpty } from '../../util/typeGuards';
import { loginSucceeded, setCookieSessionValid } from '../auth';
import {
  accountsFailed,
  applicationStartup,
  autoLoginSuccess,
  checkCookieSession,
  gatewayConnected,
  gatewayDisconnected,
  getAccountsAvailableCashFailed,
  getOwnerTradingAccountsFailed,
  individualExtendedInfoSuccess,
  logout,
  reconnectEventAction,
  servicesReady,
  setAppStatus,
  setHasSeenFirstDepositPopup,
  setHasSeenWelcomePopup,
} from '../common-actions';
import { isFirstLogin } from '../crm/helpers';
import { UserPreferencesRequest } from '../file/types';
import {
  getNonWorkingDaysAndMarketTime,
} from '../fundamental';
import { processErrorInResponse, processResponse } from '../helpers';
import { confirmInitialPriceDataLoaded, retrieveFavoriteStocksFromServerCompleted } from '../market-data';
import { getIndividualLinkedAccountsFailed, getPaymentsAllFailed } from '../payment';
import { hasValidCookie } from '../selectors';

import { USER_PREFERENCES_DATA_NAME } from './constants';
import { createBffLogoutUrl, encodeUserPreferencesData } from './helpers';
import {
  appConnected,
  appLoaded,
  getUserPreferencesData,
  getUserPreferencesDataCompleted,
  getUserPreferencesDataFailed,
  setError,
  setWebLoginReady,
  uploadUserPreferencesData,
  uploadUserPreferencesDataCompleted,
  uploadUserPreferencesDataFailed,
} from './index';
import { UserPreferencesData } from './types';

const cacheService = CacheService.getService();
const { base64 } = configLib;
const logger = LoggerService.getLogger();

type AppEpicState = Parameters<typeof hasValidCookie>[0];

const startupEpic = (action$: Observable<any>, state$: StateObservable<AppEpicState>) => action$.pipe(
  ofType(applicationStartup.type),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    GlobalService.createServicesWithoutToken();
    observer.next(checkCookieSession());
    observer.next(appLoaded());
    setTimeout(() => {
      cacheService.checkAndEvictExpiredCaches();
    }, PredefinedMilisecondsTypeEnum.twentySeconds);
  })),
);

const webLoginReadyEpic = (action$: Observable<any>, state$: StateObservable<AppEpicState>) => action$.pipe(
  ofType(setWebLoginReady.type),
  throttleTime(PredefinedMilisecondsTypeEnum.oneSecond),
  filter(() => !state$.value.app.isServicesReady),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    if (!hasValidCookie(state$.value)) return;

    GlobalService.connect();
    observer.next(servicesReady());
  })),
);

const reconnectEventEpic = (action$: Observable<any>, state$: StateObservable<AppEpicState>) => action$.pipe(
  ofType(reconnectEventAction.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    GlobalService.connect(true);
    GatewayWebSocketService.getInstance().reconnect();
  })),
);

const reconnectOnGatewayDownEpic = (action$: Observable<any>, state$: StateObservable<AppEpicState>) => action$.pipe(
  ofType(gatewayDisconnected.type),
  filter(() => hasValidCookie(state$.value)),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const appState = AppStateCache.getCurrentAppStatus();
    const customData = { appState };
    logger.error('reconnectOnGatewayDownEpic LOG', {
      statusCode: null,
      filterString: 'reconnectOnGatewayDownEpic',
      customData,
    });
    if (RECONNECT_TAPI_AND_GW_ON_GW_DOWN) {
      if (appState === 'active') {
        GlobalService.connect(true);
        GatewayWebSocketService.getInstance().reconnect();
      }
    }
  })),
);

const appStatusChangeEpic = (
  action$: Observable<PayloadAction<AppStateStatus>>,
  state$: StateObservable<AppEpicState>,
) => action$.pipe(
  ofType(setAppStatus.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const gwService = GatewayWebSocketService.getInstance();
    const isGatewayConnected = !!gwService?.isConnected();
    const isUserLoggedIn = hasValidCookie(state$.value);
    const currentAppStatus = AppStateCache.getCurrentAppStatus();
    const isPrevStateBackground = currentAppStatus === 'background';

    switch (action.payload) {
      case 'background':
        if (DISCONNECT_ON_APP_MINIMISE_OR_LOCK) {
          if (isUserLoggedIn) {
            LoaderState.setLoading(true);
          }
          const customData = { action, state: state$, isPrevStateBackground, currentAppStatus, isUserLoggedIn };
          logger.error('Disconnection happen LOG', {
            statusCode: null,
            filterString: 'appStatusChangeEpic',
            customData,
          });
          GlobalService.disconnect();
          GatewayWebSocketService.getInstance().disconnect();
        }
        break;

      case 'active':
        if (!isGatewayConnected && isUserLoggedIn && isPrevStateBackground) {
          GlobalService.connect(true);
          GatewayWebSocketService.getInstance().reconnect();
        }
        break;

      default:
        break;
    }

    // call the complete action to update Redux state after epic
    AppStateCache.setCurrentAppStatus(action.payload);
  })),
);

const refreshWebEpic = (action$: Observable<any>, state$: StateObservable<AppEpicState>) => action$.pipe(
  ofType(appLoaded.type),
  filter(() => configLib.isWeb),
  filter(() => hasValidCookie(state$.value)),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    initServices(observer, state$.value, 'refreshWebEpic');
  })),
);

const loginEpic = (action$: Observable<any>, state$: StateObservable<AppEpicState>) => action$.pipe(
  ofType(loginSucceeded.type, autoLoginSuccess.type),
  filter(() => hasValidCookie(state$.value)),
  filter(() => !state$.value.app.isServicesReady),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    initServices(observer, state$.value, 'loginEpic');
  })),
);

const pushNotificationsEpic = (action$: Observable<any>, state$: StateObservable<AppEpicState>) => action$.pipe(
  configLib.isWeb
    ? ofType(servicesReady.type)
    : ofType(loginSucceeded.type, autoLoginSuccess.type),
  filter(() => !MOCK.ENABLED),
  filter(() => hasValidCookie(state$.value)),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    configLib.PushNotificationService()?.init?.();
  })),
);

const checkAppConnectedEpic = (action$: Observable<any>, state$: StateObservable<AppEpicState>) => action$.pipe(
  ofType(
    gatewayConnected.type,
    confirmInitialPriceDataLoaded.type,
  ),
  filter(
    () => state$.value.marketData.isInitialPriceDataLoaded,
  ),
  delay(SHOW_UI_AFTER_CONNECTED_DELAY),
  mergeMap((action: any) => new Observable((observer: Observer<any>) => {
    AppStateCache.setCurrentAppStatus('active');
    observer.next(appConnected());
  })),
);

const accountsFailedEpic = (action$: Observable<any>) => action$.pipe(
  ofType(
    accountsFailed.type,
    getOwnerTradingAccountsFailed.type,
    getPaymentsAllFailed.type,
    getAccountsAvailableCashFailed.type,
    getIndividualLinkedAccountsFailed.type,
  ),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    observer.next(setError(true));
    observer.next(logout());
  })),
);

const appLogoutEpic = (action$: Observable<any>, state$: StateObservable<AppEpicState>) => action$.pipe(
  ofType(logout.type),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    // Reset all subscription services to clear listeners and instances
    ISSubscriptionService.resetInstance();
    MDSSubscriptionService.resetInstance();
    MDSTSubscriptionService.resetInstance();
    NBBOSubscriptionService.resetInstance();
    NewsSubscriptionService.resetInstance();
    SymbolNewsSubscriptionService.resetInstance();
    SymbolStatusSubscriptionService.resetInstance();

    GlobalService.authService?.signout?.().catch(error => {
      logger.log('Can not remove local user data! error:', error);
    });
    GlobalService.disconnect();
    GatewayWebSocketService.getInstance().disconnect();
    cacheService.checkAndEvictAllCaches();
    clearUserEventsLastEvent();

    const logoutUrl = createBffLogoutUrl(config.auth.bffLogoutUrl, window.location.origin);
    window.location.href = logoutUrl;
  })),
);

function initServices(observer: Observer<any>, state: AppEpicState, epicName: string) {
  if (!configLib?.store) {
    logger.error(`[app/epics] ${epicName} - store not available`);
    return;
  }

  GatewayWebSocketService.getInstance().connect();// Initialize GW-WS
  GlobalService.createServices(configLib.store.dispatch);
  if (!configLib?.isWeb) {
    GlobalService.connect();
    observer.next(servicesReady());
  }
}

const uploadUserPreferencesDataEpic = (
  action$: Observable<any>,
  state$: StateObservable<AppEpicState>,
) => action$.pipe(
  ofType(uploadUserPreferencesData.type),
  mergeMap(
    action => new Observable((observer: Observer<any>) => {
      const callName = 'app/uploadUserPreferencesData';
      const { payload } = action;
      const fileData = base64.encode(JSON.stringify(payload));
      const request: UserPreferencesRequest = {
        fileData,
        preferenceName: USER_PREFERENCES_DATA_NAME,
      };

      const { fileService } = GlobalService;
      if (!fileService) {
        logger.error('[app/uploadUserPreferencesData] File service not available');
        observer.complete();
        return;
      }
      fileService
        .uploadUserPreferenceFile(request)
        .then((response: any) => {
          processResponse(
            callName,
            {},
            response,
            observer,
            uploadUserPreferencesDataCompleted,
            uploadUserPreferencesDataFailed,
            false,
            { request: payload }, // This line is to update rootstate.app.userPreferencesData
          );
        })
        .catch((error: any) => processErrorInResponse(
          callName,
          error,
          action,
          observer,
          uploadUserPreferencesDataFailed,
        ));
    }),
  ),
);

const getUserPreferencesDataEpic = (
  action$: Observable<any>,
  state$: StateObservable<AppEpicState>,
) => action$.pipe(
  ofType(getUserPreferencesData.type, individualExtendedInfoSuccess.type),
  filter(() => hasValidCookie(state$.value)),
  mergeMap(
    action => new Observable((observer: Observer<any>) => {
      const callName = 'app/getUserPreferencesData';
      const userId = state$.value.crm?.individualExtendedInfo?.user_correlation_id;

      const { fileService } = GlobalService;
      if (!fileService) {
        logger.error('[app/getUserPreferencesData] File service not available');
        observer.complete();
        return;
      }

      const isUserFirstLogin = isFirstLogin(state$.value.crm?.individualExtendedInfo);

      fileService
        .getUserPreferenceFile(USER_PREFERENCES_DATA_NAME)
        .then((response: any) => {
          const isSuccess = processResponse(
            callName,
            {},
            response,
            observer,
            getUserPreferencesDataCompleted,
            getUserPreferencesDataFailed,
            false,
            { userId, isUserFirstLogin },
          );

          if (isSuccess) {
            observer.next(retrieveFavoriteStocksFromServerCompleted({
              ...response,
              customData: { userId, isUserFirstLogin },
            }));
          }
        })
        .catch((error: any) => processErrorInResponse(
          callName,
          error,
          action,
          observer,
          getUserPreferencesDataFailed,
        ));
    }),
  ),
);

const fetchNonWorkingDaysForFavoriteStocksEpic = (
  action$: Observable<any>,
  state$: StateObservable<AppEpicState>,
) => action$.pipe(
  ofType(retrieveFavoriteStocksFromServerCompleted.type),
  mergeMap(
    action => new Observable((observer: Observer<any>) => {
      const { data } = action.payload;
      const { favouriteStocks } = encodeUserPreferencesData(data);

      if (isEmpty(favouriteStocks)) {
        observer.complete();
        return;
      }

      const processedCountries = new Set<string>();

      favouriteStocks.forEach(stock => {
        const country = stock.countryDesignation;
        if (!processedCountries.has(country!)) {
          processedCountries.add(country!);
          observer.next(getNonWorkingDaysAndMarketTime(country!));
        }
      });

      observer.complete();
    }),
  ),
);


const cookieSessionReadyEpic = (action$: Observable<any>, state$: StateObservable<AppEpicState>) => action$.pipe(
  ofType(setCookieSessionValid.type),
  filter(action => action.payload === true),
  filter(() => !state$.value.app.isServicesReady),
  mergeMap(() => new Observable((observer: Observer<any>) => {
    initServices(observer, state$.value, 'cookieSessionReadyEpic');
    observer.next(servicesReady());
  })),
);

// dispatched on user interaction
const setHasSeenFirstDepositPopupEpic = (
  action$: Observable<any>,
  state$: StateObservable<AppEpicState>,
) => action$.pipe(
  ofType(setHasSeenFirstDepositPopup.type),
  filter(() => state$.value.app.userPreferencesData.hasSeenFirstDepositPopup !== true),
  mergeMap(
    () => new Observable((observer: Observer<any>) => {
      const updatedPreferences: UserPreferencesData = {
        ...state$.value.app.userPreferencesData,
        hasSeenFirstDepositPopup: true,
      };
      observer.next(uploadUserPreferencesData(updatedPreferences));
      observer.complete();
    }),
  ),
);

// dispatched on user interaction
const setHasSeenWelcomePopupEpic = (
  action$: Observable<any>,
  state$: StateObservable<AppEpicState>,
) => action$.pipe(
  ofType(setHasSeenWelcomePopup.type),
  filter(() => state$.value.app.userPreferencesData.hasSeenWelcomePopup !== true),
  mergeMap(
    () => new Observable((observer: Observer<any>) => {
      const updatedPreferences: UserPreferencesData = {
        ...state$.value.app.userPreferencesData,
        hasSeenWelcomePopup: true,
      };
      observer.next(uploadUserPreferencesData(updatedPreferences));
      observer.complete();
    }),
  ),
);

const epics = [
  startupEpic,
  webLoginReadyEpic,
  reconnectEventEpic,
  reconnectOnGatewayDownEpic,
  refreshWebEpic,
  loginEpic,
  pushNotificationsEpic,
  checkAppConnectedEpic,
  accountsFailedEpic,
  appStatusChangeEpic,
  cookieSessionReadyEpic,
  appLogoutEpic,
  uploadUserPreferencesDataEpic,
  getUserPreferencesDataEpic,
  fetchNonWorkingDaysForFavoriteStocksEpic,
  setHasSeenWelcomePopupEpic,
  setHasSeenFirstDepositPopupEpic,
];

export default combineEpics(...epics);
